/** Mirrors the backend's DocumentReaderUploadStatus. */
export enum DocumentReaderUploadStatus {
  UPLOADING = 'UPLOADING',
  COMPLETED = 'COMPLETED',
  ABORTED = 'ABORTED',
}

/** Mirrors the backend's DocumentReaderUploadFileStatus. ABORTED means the file can no longer be
 * resumed (its multipart upload is gone and it has no stored object). */
export enum DocumentReaderUploadFileStatus {
  UPLOADING = 'UPLOADING',
  COMPLETED = 'COMPLETED',
  ABORTED = 'ABORTED',
}

export interface IDocumentReaderUploadLimits {
  allowed: boolean;
  reason?: string;
  maxRoomBytes: number;
  maxFileCount: number;
  partSizeBytes: number;
  maxFilesPerRequest: number;
  maxPartUrlsPerRequest: number;
}

export interface IDocumentReaderUploadLimitsResponse {
  uploadLimits: IDocumentReaderUploadLimits;
}

export interface IDocumentReaderUploadFileInput {
  fileName: string;
  relativePath?: string;
  sizeBytes: number;
  contentType?: string;
}

export interface ICreateDocumentReaderUploadParams {
  conversationId?: string;
  companyId?: number;
  files: IDocumentReaderUploadFileInput[];
}

export interface IDocumentReaderCreatedFile {
  id: string;
  fileName: string;
  partCount: number;
  partSizeBytes: number;
}

export interface IDocumentReaderUploadBatchCreated {
  id: string;
  batchIndex: number;
  files: IDocumentReaderCreatedFile[];
  skippedEmptyFileNames: string[];
}

export interface IDocumentReaderUploadBatchCreatedResponse {
  upload: IDocumentReaderUploadBatchCreated;
}

export interface IDocumentReaderPartUrl {
  partNumber: number;
  url: string;
  expiresAt: string;
}

export interface IDocumentReaderPartUrlsResponse {
  partUrls: IDocumentReaderPartUrl[];
}

/** One stored part as the browser sends it on completion: the ETag header its PUT answered. */
export interface IDocumentReaderUploadPart {
  partNumber: number;
  entityTag: string;
}

export interface IDocumentReaderUploadedPart extends IDocumentReaderUploadPart {
  sizeBytes: number;
}

export interface IDocumentReaderUploadFile {
  id: string;
  fileName: string;
  relativePath: string | null;
  sizeBytes: number;
  status: DocumentReaderUploadFileStatus;
  partCount: number;
  partSizeBytes: number;
  uploadedParts: IDocumentReaderUploadedPart[];
  missingPartNumbers: number[];
}

/** One batch of an upload with its files' stored parts, read to resume after a reload. */
export interface IDocumentReaderUploadBatch {
  id: string;
  status: DocumentReaderUploadStatus;
  fileCount: number;
  completedFileCount: number;
  totalSizeBytes: number;
  completedSizeBytes: number;
  batchCount: number;
  batchIndex: number;
  files: IDocumentReaderUploadFile[];
}

export interface IDocumentReaderUploadBatchResponse {
  upload: IDocumentReaderUploadBatch;
}

/** SWR key of a sent room's files: a name, the upload id and its file count. */
export type TDocumentReaderUploadFilesKey = [string, string, number];

export interface IDocumentReaderUploadFilesState {
  uploadFiles: IDocumentReaderUploadFile[] | null;
  isLoading: boolean;
  error: unknown;
}

export interface IDocumentReaderUploadStatusView {
  id: string;
  status: DocumentReaderUploadStatus;
}

export interface ICompleteDocumentReaderUploadFileResponse {
  file: { id: string; status: DocumentReaderUploadFileStatus };
  upload: IDocumentReaderUploadStatusView;
}

/** An upload's status with its counts, as the backend answers after removing one file. */
export interface IDocumentReaderUploadCountsView
  extends IDocumentReaderUploadStatusView {
  fileCount: number;
  totalSizeBytes: number;
  completedFileCount: number;
  completedSizeBytes: number;
}

export interface IRemoveDocumentReaderUploadFileResponse {
  upload: IDocumentReaderUploadCountsView;
}

export interface IAbortDocumentReaderUploadResponse {
  upload: IDocumentReaderUploadStatusView;
}

export interface IPutDocumentReaderPartParams {
  url: string;
  blob: Blob;
  onProgress?: (loadedBytes: number) => void;
  signal?: AbortSignal;
}

/** A failed PUT keeps its HTTP status: a 2xx without an ETag is the bucket CORS hiding the header,
 * which no retry can fix. */
export type TPutDocumentReaderPartResult =
  | { success: true; entityTag: string }
  | { success: false; status: number; error: string };

/** What a chat message carries for a data room, the same fields the backend stores in its
 * `data-document-upload` message part. */
export interface IDocumentReaderUploadReference {
  uploadId: string;
  fileCount: number;
  totalSizeBytes: number;
}

/** Mirrors the backend's DocumentReaderRunState. */
export enum DocumentReaderRunState {
  QUEUED = 'QUEUED',
  RUNNING = 'RUNNING',
  AWAITING_APPROVAL = 'AWAITING_APPROVAL',
  COMPLETED = 'COMPLETED',
  STOPPED = 'STOPPED',
  FAILED = 'FAILED',
}

/** Mirrors the backend's DocumentReaderRunStage, in the order a run moves through them. */
export enum DocumentReaderRunStage {
  STORE = 'STORE',
  UNPACK = 'UNPACK',
  CHECK_FILES = 'CHECK_FILES',
  ESTIMATE = 'ESTIMATE',
  READ = 'READ',
  FIND = 'FIND',
  CARD = 'CARD',
  CUT = 'CUT',
  EXTRACT = 'EXTRACT',
  CHECK_VALUES = 'CHECK_VALUES',
  GROUP = 'GROUP',
  MATCH = 'MATCH',
  DONE = 'DONE',
}

/** Mirrors the backend's IDocumentReaderRunCounters. */
export interface IDocumentReaderRunCounters {
  filesReceived: number;
  filesRead: number;
  filesCopied: number;
  filesNotSupported: number;
  filesFailed: number;
  unpackedBytes: number;
  pages: number;
  pagesRead: number;
  documents: number;
  companies: number;
  valuesChecked: number;
  valuesFlagged: number;
  modelCallResentCount: number;
  storedFileCount: number;
  storedBytes: number;
  /** The archive's own entry count, once known; absent on runs from before live unzip counts. */
  filesExpected?: number;
  /** Updated while unzipping, together with unpackedBytes. */
  filesUnpacked?: number;
  /** Updated while checking files, out of filesUnpacked; absent on runs from before live check counts. */
  filesChecked?: number;
  /** Updated while understanding the documents (find to check values); absent on older runs. */
  documentsUnderstood?: number;
  documentsToUnderstand?: number;
  /** Updated while sorting by company (group and match); absent on older runs. */
  companiesSorted?: number;
  companiesToSort?: number;
}

export interface IDocumentReaderRunEstimateLine {
  item: string;
  modelId: string | null;
  calls: number;
  costUsd: string;
  bytes: number | null;
}

/** Written once the files are checked, before any paid reading. costUsd covers model calls only, with the margin. */
export interface IDocumentReaderRunEstimate {
  pagesToRead: number;
  modelCallCount: number;
  costUsd: string;
  estimatedAt: string;
  lines: IDocumentReaderRunEstimateLine[];
}

/** Mirrors the backend's DocumentReaderWriteBackState. */
export enum DocumentReaderWriteBackState {
  QUEUED = 'QUEUED',
  RUNNING = 'RUNNING',
  DONE = 'DONE',
  FAILED = 'FAILED',
}

/** Mirrors the backend's IDocumentReaderWriteBackProgressView: the run's newest write-back, counted by company. */
export interface IDocumentReaderWriteBackProgress {
  proposalId: string;
  state: DocumentReaderWriteBackState;
  total: number;
  done: number;
  created: number;
  updated: number;
  skipped: number;
  needsInfo: number;
  clashes: number;
  error: string | null;
  updatedAt: string;
  /** The company being written now and its items; absent on write-backs from before live item counts. */
  currentCompany?: string | null;
  itemsDone?: number;
  itemsTotal?: number;
}

/** Mirrors the backend's DocumentReaderReExtractState. */
export enum DocumentReaderReExtractState {
  RUNNING = 'RUNNING',
  DONE = 'DONE',
  STOPPED = 'STOPPED',
  FAILED = 'FAILED',
}

/** The part of the backend's IDocumentReaderRunReExtractProgress the run strip reads: a second pass over a finished run. */
export interface IDocumentReaderReExtractProgress {
  state: DocumentReaderReExtractState;
  documentsToRedo: number;
  /** Absent on passes from before live re-read counts. */
  papersDone?: number;
}

/** Mirrors the backend's IDocumentReaderRunView: one run's state, what it has found and what it has spent. */
export interface IDocumentReaderRunView {
  runId: string;
  state: DocumentReaderRunState;
  stage: DocumentReaderRunStage;
  counters: IDocumentReaderRunCounters;
  completedModelCalls: number;
  failedModelCalls: number;
  costUsd: string;
  estimate: IDocumentReaderRunEstimate | null;
  /** Only while pages are being read, and only once a reading speed is known. */
  readingSecondsLeft: number | null;
  failureMessage: string | null;
  stopReason: string | null;
  resumeCount: number;
  startedAt: string | null;
  finishedAt: string | null;
  updatedAt: string;
  /** Only on a COMPLETED run, and null until a write-back has started. */
  writeBack?: IDocumentReaderWriteBackProgress | null;
  /** The newest re-read of a finished run, null when none was started. */
  reExtract?: IDocumentReaderReExtractProgress | null;
}

export interface IDocumentReaderRunResponse {
  run: IDocumentReaderRunView;
}

export interface IUseDocumentReaderRunResult {
  run: IDocumentReaderRunView | null;
  isLoading: boolean;
  error: unknown;
  refreshRun: () => Promise<void>;
}

export interface IDocumentReaderContinueRunResult {
  runId: string;
  state: DocumentReaderRunState;
}

export interface IContinueDocumentReaderRunResponse {
  run: IDocumentReaderContinueRunResult;
}

/** A short-lived link to one document the chat named (D-38); wholeFile when that document shares its file (a spreadsheet or Word file) with others. */
export interface IDocumentReaderPaperViewUrl {
  url: string;
  expiresAt: string;
  wholeFile: boolean;
}

export interface IDocumentReaderPaperViewUrlResponse {
  view: IDocumentReaderPaperViewUrl;
}
